/**
 * Admin Badge Management Page
 * Provides interface for managing badge definitions with CRUD operations
 * Uses TanStack Table for data display, search, and actions
 */

import { useState, useMemo } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  type SortingState,
} from '@tanstack/react-table';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useAdminBadgesList, useDeleteBadge } from '../../hooks/useBadgeApi';
import { type BadgeDefinition } from '../../lib/types/badge';
import { BadgeFormModal } from '../../components/admin/BadgeFormModal';
import { parseTimestamp } from '../../lib/utils/formatters';
import { BadgeIcon } from '../../components/badges/BadgeIcon';

const columnHelper = createColumnHelper<BadgeDefinition>();

export function BadgeManagementPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedBadge, setSelectedBadge] = useState<BadgeDefinition | null>(
    null
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null
  );

  // Fetch badges
  const {
    data: badgesData,
    isLoading,
    error,
    refetch,
  } = useAdminBadgesList({
    limit: 50,
  });

  const deleteBadge = useDeleteBadge();

  // Filter badges based on search query
  const filteredBadges = useMemo(() => {
    if (!badgesData?.badges || !searchQuery) {
      return badgesData?.badges || [];
    }

    const query = searchQuery.toLowerCase();
    return badgesData.badges.filter(
      (badge: BadgeDefinition) =>
        badge.name.toLowerCase().includes(query) ||
        badge.badgeId.toLowerCase().includes(query) ||
        badge.description.toLowerCase().includes(query)
    );
  }, [badgesData?.badges, searchQuery]);

  // Table columns definition
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Badge',
        cell: ({ row, getValue }) => (
          <div className="flex items-center space-x-3">
            {/* This table is where a freshly created, icon-less badge is
                certain to appear, so it uses the shared BadgeIcon rather than
                its own inline fallback. */}
            <div className="flex-shrink-0 w-8 h-8">
              <BadgeIcon
                src={row.original.icon_url}
                alt={getValue()}
                size="sm"
                className="rounded-full"
                imageless={row.original.imageless}
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {getValue()}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {row.original.badgeId}
              </p>
            </div>
          </div>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: ({ getValue }) => (
          <p className="text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate">
            {getValue()}
          </p>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('created_at', {
        header: 'Created',
        cell: ({ getValue }) => {
          // Use parseTimestamp utility to safely handle Firestore Timestamp formats
          const date = parseTimestamp(getValue());
          if (!date) {
            return <span className="text-sm text-gray-400">-</span>;
          }
          return (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {date.toLocaleDateString()}
            </span>
          );
        },
        enableSorting: true,
        sortingFn: (rowA, rowB, columnId) => {
          const dateA = parseTimestamp(rowA.getValue(columnId));
          const dateB = parseTimestamp(rowB.getValue(columnId));
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA.getTime() - dateB.getTime();
        },
      }),
      columnHelper.accessor('updated_at', {
        header: 'Updated',
        cell: ({ getValue }) => {
          // Use parseTimestamp utility to safely handle Firestore Timestamp formats
          const date = parseTimestamp(getValue());
          if (!date) {
            return <span className="text-sm text-gray-400">Never</span>;
          }
          return (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {date.toLocaleDateString()}
            </span>
          );
        },
        enableSorting: true,
        sortingFn: (rowA, rowB, columnId) => {
          const dateA = parseTimestamp(rowA.getValue(columnId));
          const dateB = parseTimestamp(rowB.getValue(columnId));
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA.getTime() - dateB.getTime();
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex space-x-2">
            <button
              onClick={() => setSelectedBadge(row.original)}
              className="text-sky-600 hover:text-sky-900 dark:text-sky-400 dark:hover:text-sky-300 text-sm font-medium"
            >
              Edit
            </button>
            <button
              onClick={() => setShowDeleteConfirm(row.original.badgeId)}
              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
            >
              Delete
            </button>
          </div>
        ),
      }),
    ],
    []
  );

  // Initialize table
  const table = useReactTable({
    data: filteredBadges,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Handle search input
  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const query = formData.get('search') as string;
    setSearchQuery(query.trim());
  };

  const handleCreateBadge = () => {
    setShowCreateModal(true);
  };

  const handleDeleteBadge = async (badgeId: string) => {
    try {
      await deleteBadge.mutateAsync(badgeId);
      setShowDeleteConfirm(null);
    } catch (_error) {
      // Error is handled by the mutation
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Badge Management
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage badge definitions and configurations
            </p>
          </div>
          <button
            onClick={handleCreateBadge}
            className="self-start inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Badge
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-sky-100 dark:bg-sky-900 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-sky-600 dark:text-sky-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Total Badges
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {badgesData?.total || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-green-600 dark:text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Active Badges
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {badgesData?.badges?.length || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-purple-600 dark:text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Badges Awarded
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {/* TODO: Add badge assignment count */}-
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search Form */}
              <form onSubmit={handleSearch} className="flex-1">
                <div className="flex">
                  <input
                    type="text"
                    name="search"
                    placeholder="Search badges by name, ID, or description..."
                    defaultValue={searchQuery}
                    className="block w-full rounded-l-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                  />
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                </div>
              </form>

              {/* Clear Search */}
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Results Info */}
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {isLoading ? (
                  'Loading...'
                ) : error ? (
                  <span className="text-red-600 dark:text-red-400">
                    Error loading badges
                  </span>
                ) : (
                  <>
                    Showing{' '}
                    <span className="font-medium">{filteredBadges.length}</span>{' '}
                    of{' '}
                    <span className="font-medium">
                      {badgesData?.total || 0}
                    </span>{' '}
                    badges
                  </>
                )}
              </p>
              {error && (
                <button
                  onClick={() => refetch()}
                  className="text-sm font-medium text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300"
                >
                  Retry
                </button>
              )}
            </div>
          </div>

          {/* Badges Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-gray-500 dark:text-gray-400 mb-2">
                    Failed to load badges
                  </p>
                  <button
                    onClick={() => refetch()}
                    className="text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300 font-medium"
                  >
                    Try again
                  </button>
                </div>
              </div>
            ) : !filteredBadges.length ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500 dark:text-gray-400">
                  {searchQuery
                    ? 'No badges match your search'
                    : 'No badges found'}
                </p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <div className="flex items-center space-x-1">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getCanSort() && (
                              <span className="text-gray-400">
                                {{
                                  asc: '↑',
                                  desc: '↓',
                                }[header.column.getIsSorted() as string] ??
                                  '↕️'}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {table.getRowModel().rows.map(row => (
                    <tr
                      key={row.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Badge Create/Edit Modal */}
      <BadgeFormModal
        isOpen={showCreateModal || !!selectedBadge}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedBadge(null);
        }}
        badge={selectedBadge}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Delete Badge
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete this badge? This action cannot be
              undone.
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Badge ID: {showDeleteConfirm}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                disabled={deleteBadge.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteBadge(showDeleteConfirm)}
                disabled={deleteBadge.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
              >
                {deleteBadge.isPending ? 'Deleting...' : 'Delete Badge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
